import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faBucket } from "@fortawesome/pro-solid-svg-icons/faBucket";
import { faLocationPen } from "@fortawesome/pro-solid-svg-icons/faLocationPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { ReactComponent as TerritoriesIcon } from "@app/assets/map/legends/territoriesDisabled.svg";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import MapMode, { displayNames } from "@app/scene/map/enums/MapMode";
import { showPinSettings } from "@app/scene/map/store/actions";
import {
  getMapMode,
  getTerritoryMode,
  isPinLegendSettingsOpen,
} from "@app/scene/map/store/selectors";
import TerritoryModeState from "@app/scene/map/store/territoryMode/TerritoryModeState";
import { doesOrganizationSupportTerritories, isCurrentUserMember } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import GroupsModal from "./components/GroupsModal";
import PinSettingsPopover from "./components/PinSettingsPopover";
import TerritoriesPopover from "./components/TerritoriesPopover";
import styles from "./Toolbar.module.scss";

enum SubMenu {
  PIN_SETTINGS = "pinSettings",
  TERRITORIES = "territories",
  GROUPS = "groups",
}

const messages = defineMessages({
  groups: {
    id: "map.toolbar.buttons.groups",
    defaultMessage: "Groups",
    description: "Title of the Groups toolbar item",
  },
  pinSettings: {
    id: "map.toolbar.buttons.pinSettings",
    defaultMessage: "Pin Settings",
    description: "Title of the Pin Settings toolbar item",
  },
  territories: {
    id: "map.toolbar.buttons.territories",
    defaultMessage: "Territories",
    description: "Title of the Territories toolbar item",
  },
  trackReps: {
    id: "map.toolbar.buttons.trackReps",
    defaultMessage: "Track Reps",
    description: "Title of the Track Reps toolbar item",
  },
});

const disabledMessages = defineMessages({
  pinSettings: {
    id: "map.toolbar.buttons.pinSettings.disabled",
    defaultMessage: "Exit {mode} to Edit Pins",
    description: "Title of the Pin Settings toolbar item when in groups mode",
  },
});

interface Props {
  disabled?: boolean;
  isPinLegendSettingsOpen?: boolean;
  mode?: MapMode;
  onShowPinSettings?: typeof showPinSettings;
  showTerritoriesItem: boolean;
  territoryMode: TerritoryModeState["mode"];
}

const Toolbar: React.FC<Props> = ({
  disabled,
  isPinLegendSettingsOpen,
  mode,
  onShowPinSettings,
  showTerritoriesItem,
  territoryMode,
}) => {
  const toolbarAnalytics = useExtendedAnalytics("Toolbar", useAnalytics());
  const intl = useIntl();
  const [openSubMenu, setOpenSubMenu] = useState<SubMenu | undefined>();
  const [openTooltip, setOpenTooltip] = useState<SubMenu | undefined>();

  useEffect(() => {
    if (isPinLegendSettingsOpen) {
      setOpenSubMenu(SubMenu.PIN_SETTINGS);
    }
  }, [isPinLegendSettingsOpen]);

  const handleToggleSubmenu = useCallback(
    (key: SubMenu, open: boolean) => {
      if (!open) {
        if (key === SubMenu.PIN_SETTINGS) {
          onShowPinSettings?.(false);
        }
        setOpenSubMenu(undefined);
        return;
      }
      setOpenSubMenu(key);
      setOpenTooltip(undefined);
      switch (key) {
        case SubMenu.GROUPS:
          toolbarAnalytics.clicked(["Groups"]);
          break;
        case SubMenu.PIN_SETTINGS:
          toolbarAnalytics.clicked(["Pin Settings"]);
          break;
        case SubMenu.TERRITORIES:
          toolbarAnalytics.clicked(["Territories"]);
          break;
      }
    },
    [onShowPinSettings, toolbarAnalytics]
  );

  const handleToggleTooltip = useCallback((key: SubMenu, open: boolean) => {
    setOpenTooltip(open ? key : undefined);
  }, []);

  const handleHideAll = useCallback(() => setOpenSubMenu(undefined), []);

  const handleGroupsOpen = useCallback(() => {
    const open = openSubMenu === SubMenu.GROUPS;
    handleToggleSubmenu(SubMenu.GROUPS, !open);
  }, [handleToggleSubmenu, openSubMenu]);

  const disabledPinSettings =
    mode === MapMode.GROUPS || (mode === MapMode.TERRITORIES && territoryMode === "view");
  const disabledGroups = mode === MapMode.LEAD_FINDER;
  const disabledTerritories = mode === MapMode.LEAD_FINDER;

  return (
    <div className={styles.container}>
      <Interactive>
        <div className={styles.menu}>
          <Popover
            content={<PinSettingsPopover onHide={handleHideAll} />}
            onOpenChange={(open: boolean) => handleToggleSubmenu(SubMenu.PIN_SETTINGS, open)}
            open={openSubMenu === SubMenu.PIN_SETTINGS}
            placement="rightTop"
            trigger={disabled || disabledPinSettings ? [] : ["click"]}
          >
            <Tooltip
              mouseEnterDelay={0}
              mouseLeaveDelay={0}
              onOpenChange={(open: boolean) => handleToggleTooltip(SubMenu.PIN_SETTINGS, open)}
              open={openTooltip === SubMenu.PIN_SETTINGS}
              placement="right"
              title={
                disabledPinSettings && mode
                  ? intl.formatMessage(disabledMessages.pinSettings, {
                      mode: intl.formatMessage(displayNames[mode]),
                    })
                  : intl.formatMessage(messages.pinSettings)
              }
              trigger={openSubMenu === SubMenu.PIN_SETTINGS ? [] : ["hover"]}
            >
              <div
                className={cn(styles.menuItem, {
                  [styles.disabled]: disabled || disabledPinSettings,
                })}
                data-menu-item="pinSettings"
              >
                <FontAwesomeIcon className={styles.pinSettingsIcon} icon={faLocationPen} />
              </div>
            </Tooltip>
          </Popover>

          {showTerritoriesItem && (
            <Popover
              content={<TerritoriesPopover onHide={handleHideAll} />}
              onOpenChange={(open: boolean) => handleToggleSubmenu(SubMenu.TERRITORIES, open)}
              open={openSubMenu === SubMenu.TERRITORIES}
              placement="rightTop"
              trigger={disabled || disabledTerritories ? [] : ["click"]}
            >
              <Tooltip
                mouseEnterDelay={0}
                mouseLeaveDelay={0}
                onOpenChange={(open: boolean) => handleToggleTooltip(SubMenu.TERRITORIES, open)}
                open={openTooltip === SubMenu.TERRITORIES}
                placement="right"
                title={intl.formatMessage(messages.territories)}
                trigger={openSubMenu === SubMenu.TERRITORIES ? [] : ["hover"]}
              >
                <div
                  className={cn(styles.menuItem, {
                    [styles.disabled]: disabled || disabledTerritories,
                  })}
                  data-menu-item="territories"
                >
                  <TerritoriesIcon />
                </div>
              </Tooltip>
            </Popover>
          )}
          <GroupsModal onHide={handleHideAll} open={openSubMenu === SubMenu.GROUPS} />
          <Tooltip
            mouseEnterDelay={0}
            mouseLeaveDelay={0}
            onOpenChange={(open: boolean) => handleToggleTooltip(SubMenu.GROUPS, open)}
            open={openTooltip === SubMenu.GROUPS}
            placement="right"
            title={intl.formatMessage(messages.groups)}
            trigger={openSubMenu === SubMenu.GROUPS ? [] : ["hover"]}
          >
            <div
              className={cn(styles.menuItem, {
                [styles.disabled]: disabled || disabledGroups,
              })}
              data-menu-item="groups"
              onClick={handleGroupsOpen}
            >
              <FontAwesomeIcon className={styles.groupsIcon} icon={faBucket} />
            </div>
          </Tooltip>
        </div>
      </Interactive>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isPinLegendSettingsOpen: isPinLegendSettingsOpen(state),
  mode: getMapMode(state),
  // showing terr item if user is owner/manager to upsell it
  showTerritoriesItem: doesOrganizationSupportTerritories(state) || !isCurrentUserMember(state),
  territoryMode: getTerritoryMode(state),
});

const mapDispatchToProps = {
  onShowPinSettings: showPinSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(Toolbar);
